import { useEffect, useState } from 'react';
import api from '../../services/api';
import { toast } from 'sonner';
import {
  RefreshCw, Zap, CheckCircle2, AlertTriangle, XCircle, Gamepad2,
  Clock, Wallet, Layers, Activity, Loader2,
} from 'lucide-react';

const fmtUSD = (v) =>
  v === null || v === undefined
    ? '—'
    : `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

const fmtTime = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

export default function ProviderBalancePage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshingAll, setRefreshingAll] = useState(false);
  // Per-provider action state: { [code]: { checking: bool, testing: bool, testResult: {...} } }
  const [rowState, setRowState] = useState({});

  const fetchCached = async () => {
    try {
      const res = await api.get('/api/admin/provider-balance/');
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load provider balances');
    } finally {
      setLoading(false);
    }
  };

  const refreshAllLive = async () => {
    setRefreshingAll(true);
    try {
      const res = await api.get('/api/admin/provider-balance/?live=true');
      setData(res.data);
      toast.success('Live sweep complete — balances refreshed');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Refresh failed');
    } finally {
      setRefreshingAll(false);
    }
  };

  const checkOne = async (code) => {
    setRowState((s) => ({ ...s, [code]: { ...(s[code] || {}), checking: true } }));
    try {
      const res = await api.post(`/api/admin/provider-balance/${code}/check`);
      // Patch the local row with the fresh data
      setData((prev) => {
        if (!prev) return prev;
        const items = prev.items.map((r) =>
          r.provider_code === code
            ? {
                ...r,
                balance: res.data.balance,
                ok: res.data.ok,
                error: res.data.error,
                last_checked_at: res.data.checked_at,
                is_low: !!(res.data.ok && res.data.balance < 500),
              }
            : r
        );
        const healthy = items.filter((r) => r.ok).length;
        const total = items.reduce((a, r) => a + (r.ok && r.balance ? r.balance : 0), 0);
        return {
          ...prev,
          items,
          healthy_providers: healthy,
          total_balance_usd: total,
          low_balance_count: items.filter((r) => r.ok && r.balance < 500).length,
        };
      });
      if (res.data.ok) {
        toast.success(`${code} balance: ${fmtUSD(res.data.balance)}`);
      } else {
        toast.error(`${code} check failed: ${res.data.error || 'unknown'}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Check failed');
    } finally {
      setRowState((s) => ({ ...s, [code]: { ...(s[code] || {}), checking: false } }));
    }
  };

  const testOne = async (code) => {
    setRowState((s) => ({
      ...s,
      [code]: { ...(s[code] || {}), testing: true, testResult: null },
    }));
    try {
      const res = await api.post(`/api/admin/provider-balance/${code}/test-connection`);
      setRowState((s) => ({
        ...s,
        [code]: { ...(s[code] || {}), testing: false, testResult: res.data },
      }));
      if (res.data.ok) {
        toast.success(`${code} connection OK (${res.data.duration_ms} ms)`);
      } else {
        toast.error(
          `${code} connection failed: ${res.data.error || 'handler not registered'}`
        );
      }
    } catch (e) {
      setRowState((s) => ({ ...s, [code]: { ...(s[code] || {}), testing: false } }));
      toast.error(e?.response?.data?.detail || 'Test failed');
    }
  };

  useEffect(() => {
    fetchCached();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="provider-balance-loading">
        <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
      </div>
    );
  }

  if (!data) return null;
  const items = data.items || [];
  const lowCount = data.low_balance_count ?? 0;

  return (
    <div className="space-y-6" data-testid="provider-balance-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <Wallet className="w-6 h-6 text-[#D4AF37]" /> Provider Balance
          </h2>
          <p className="text-xs text-gray-400 mt-1">
            Live upstream game-provider pool balances. Auto-refreshes every hour; master
            admins get low-balance alerts when any provider drops below&nbsp;
            <span className="text-amber-400 font-semibold">$500</span>.
          </p>
        </div>
        <button
          onClick={refreshAllLive}
          disabled={refreshingAll}
          className="flex items-center gap-2 px-4 py-2 rounded-xl bg-[#D4AF37] text-black font-semibold hover:bg-[#F5D76E] disabled:opacity-50 transition"
          data-testid="refresh-all-balances-btn"
        >
          {refreshingAll ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <RefreshCw className="w-4 h-4" />
          )}
          {refreshingAll ? 'Sweeping…' : 'Refresh All (live)'}
        </button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="provider-balance-summary">
        <SummaryTile
          icon={Wallet}
          label="Combined Pool"
          value={fmtUSD(data.total_balance_usd)}
          color="#22c55e"
          testid="summary-total"
        />
        <SummaryTile
          icon={CheckCircle2}
          label="Healthy / Total"
          value={`${data.healthy_providers} / ${data.total_providers}`}
          color="#3b82f6"
          testid="summary-healthy"
        />
        <SummaryTile
          icon={AlertTriangle}
          label={`Low (<$500)`}
          value={lowCount}
          color={lowCount ? '#f59e0b' : '#6b7280'}
          testid="summary-low"
        />
        <SummaryTile
          icon={Clock}
          label="Auto-refresh"
          value="every hour"
          color="#8b5cf6"
          testid="summary-auto"
          small
        />
      </div>

      {/* Provider cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="provider-cards-grid">
        {items.map((p) => (
          <ProviderCard
            key={p.provider_code}
            row={p}
            state={rowState[p.provider_code] || {}}
            onCheck={() => checkOne(p.provider_code)}
            onTest={() => testOne(p.provider_code)}
          />
        ))}
      </div>

      {items.length === 0 && (
        <div className="rounded-2xl p-8 bg-[#131822] border border-dashed border-white/10 text-center text-gray-400">
          <Layers className="w-12 h-12 mx-auto mb-3 text-gray-600" />
          No game providers configured yet.
        </div>
      )}
    </div>
  );
}

function SummaryTile({ icon: Icon, label, value, color, testid, small }) {
  return (
    <div
      className="rounded-2xl p-4 bg-[#131822] border border-white/10 flex items-center gap-3"
      data-testid={testid}
    >
      <div
        className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
        style={{ background: `${color}18` }}
      >
        <Icon className="w-5 h-5" style={{ color }} />
      </div>
      <div className="min-w-0">
        <p className="text-[10px] uppercase tracking-wider text-gray-500">{label}</p>
        <p
          className={`${small ? 'text-base' : 'text-2xl'} font-bold tabular-nums text-white truncate`}
        >
          {value}
        </p>
      </div>
    </div>
  );
}

function ProviderCard({ row, state, onCheck, onTest }) {
  const { checking, testing, testResult } = state;
  const isLow = row.ok && typeof row.balance === 'number' && row.balance < 500;
  const isFailed = !row.ok;

  const ringColor = isFailed
    ? 'border-red-500/30 bg-red-500/5'
    : isLow
      ? 'border-amber-500/30 bg-amber-500/5'
      : 'border-white/10 bg-[#131822]';

  const StatusIcon = isFailed ? XCircle : isLow ? AlertTriangle : CheckCircle2;
  const statusColor = isFailed ? '#ef4444' : isLow ? '#f59e0b' : '#22c55e';

  return (
    <div
      className={`rounded-2xl p-4 border ${ringColor} transition`}
      data-testid={`provider-card-${row.provider_code}`}
    >
      <div className="flex items-start gap-3">
        {row.logo_url ? (
          <img
            src={row.logo_url}
            alt={row.provider_name}
            className="w-14 h-14 rounded-xl object-cover flex-shrink-0"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
          />
        ) : (
          <div className="w-14 h-14 rounded-xl bg-white/5 flex items-center justify-center flex-shrink-0">
            <Gamepad2 className="w-7 h-7 text-gray-500" />
          </div>
        )}

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h4 className="text-base font-semibold text-white truncate">
              {row.provider_name}
            </h4>
            <StatusIcon className="w-4 h-4 flex-shrink-0" style={{ color: statusColor }} />
          </div>
          <p className="text-[11px] text-gray-500 font-mono mt-0.5">{row.provider_code}</p>

          <div className="mt-2">
            {row.ok ? (
              <p
                className={`text-3xl font-bold tabular-nums ${
                  isLow ? 'text-amber-400' : 'text-green-400'
                }`}
                data-testid={`balance-${row.provider_code}`}
              >
                {fmtUSD(row.balance)}
              </p>
            ) : (
              <p
                className="text-xs text-red-300 leading-tight"
                data-testid={`error-${row.provider_code}`}
              >
                {row.error || 'No balance data yet — click Check Balance.'}
              </p>
            )}
            <p className="text-[10px] text-gray-500 mt-1 flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {row.last_checked_at ? `Checked ${fmtTime(row.last_checked_at)}` : 'Never checked'}
            </p>
          </div>
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex items-center gap-2 mt-3">
        <button
          onClick={onCheck}
          disabled={checking}
          className="flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-[#D4AF37] text-xs font-semibold hover:bg-[#D4AF37]/20 disabled:opacity-40 transition"
          data-testid={`check-balance-btn-${row.provider_code}`}
        >
          {checking ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <Wallet className="w-3.5 h-3.5" />
          )}
          {checking ? 'Checking…' : 'Check Balance'}
        </button>
        <button
          onClick={onTest}
          disabled={testing}
          className="flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-blue-500/10 border border-blue-500/30 text-blue-400 text-xs font-semibold hover:bg-blue-500/20 disabled:opacity-40 transition"
          data-testid={`test-connection-btn-${row.provider_code}`}
        >
          {testing ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <Activity className="w-3.5 h-3.5" />
          )}
          {testing ? 'Testing…' : 'Test Connection'}
        </button>
      </div>

      {/* Test result inline */}
      {testResult && (
        <div
          className={`mt-3 rounded-lg border px-3 py-2 text-[11px] ${
            testResult.ok
              ? 'bg-green-500/5 border-green-500/20 text-green-300'
              : 'bg-red-500/5 border-red-500/20 text-red-300'
          }`}
          data-testid={`test-result-${row.provider_code}`}
        >
          {testResult.ok ? (
            <span className="flex items-center gap-1.5">
              <Zap className="w-3.5 h-3.5" />
              Connection OK · handler registered · {testResult.duration_ms} ms
            </span>
          ) : (
            <span className="flex items-center gap-1.5">
              <XCircle className="w-3.5 h-3.5" />
              {testResult.error ||
                (testResult.handler_registered === false
                  ? 'Handler not registered on upstream'
                  : 'Upstream not reachable')}
            </span>
          )}
        </div>
      )}

      {/* Low-balance warning footer */}
      {isLow && (
        <div className="mt-2 text-[11px] text-amber-300 flex items-center gap-1.5">
          <AlertTriangle className="w-3.5 h-3.5" />
          Below $500 threshold — top up required.
        </div>
      )}
    </div>
  );
}
